import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { KeyboardShortcut, LoadingIndicator, Row } from "@/components";
import { FormButtonGroup } from "@/components/form/FormButtonGroup";
import { FormDateTimePicker } from "@/components/form/FormDateTimePicker";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormSwitchInput } from "@/components/form/FormSwitchInput";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useCreateAddInterview } from "@/hooks/createAdds/useCreateAddInterview";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import {
  defaultInterviewQueryParams,
  interviewDurationOptions,
  interviewStatusOptions,
  timezoneOptions,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { dates } from "@/lib/dates";
import {
  CandidacyStatus,
  InterviewDialogMode,
  InterviewDuration,
  InterviewStatus,
  InterviewType,
  Timezone,
} from "@/types";

interface ModeConfig {
  title: string;
  interviewType: InterviewType;
  candidacyStatus: (status: InterviewStatus | undefined) => CandidacyStatus;
  defaultInterviewTitle: string;
  defaultStatus: InterviewStatus | undefined;
  defaultDurationMinutes: InterviewDuration | undefined;
  defaultTimezone: Timezone | undefined;
  getDefaultDateTime: () => string;
  showStatusSelect: boolean;
  statusOptionValues?: InterviewStatus[];
  shouldCreateCalendarEvent: (status: InterviewStatus | undefined) => boolean;
  showVideoSwitch: boolean;
  submitMixpanelEvent: string;
}

// When moving a candidacy straight to "Screened", default the logged screen's
// time to this many minutes ago (rounded to the nearest 5).
const SCREENED_DEFAULT_MINUTES_AGO = 15;

const modeConfig: Record<InterviewDialogMode, ModeConfig> = {
  [InterviewDialogMode.INTERVIEW]: {
    title: "Schedule Interview",
    interviewType: InterviewType.CUSTOMER,
    candidacyStatus: () => CandidacyStatus.INTERVIEW,
    defaultInterviewTitle: "Interview",
    defaultStatus: undefined,
    defaultDurationMinutes: undefined,
    defaultTimezone: undefined,
    getDefaultDateTime: () => "",
    showStatusSelect: true,
    shouldCreateCalendarEvent: () => false,
    showVideoSwitch: false,
    submitMixpanelEvent: "Schedule interview",
  },
  [InterviewDialogMode.SCREEN]: {
    title: "Add Screen",
    interviewType: InterviewType.INTERNAL,
    candidacyStatus: (status) =>
      status === InterviewStatus.COMPLETED
        ? CandidacyStatus.SCREENED
        : CandidacyStatus.SCREENSCHEDULED,
    defaultInterviewTitle: "Initial Phone Screen",
    defaultStatus: undefined,
    defaultDurationMinutes: InterviewDuration.MINUTES_15,
    defaultTimezone: undefined,
    getDefaultDateTime: () => "",
    showStatusSelect: true,
    statusOptionValues: [
      InterviewStatus.SCHEDULED,
      InterviewStatus.CANDIDATE_CONFIRMED,
      InterviewStatus.COMPLETED,
    ],
    shouldCreateCalendarEvent: (status) => status !== InterviewStatus.COMPLETED,
    showVideoSwitch: true,
    submitMixpanelEvent: "Add screen",
  },
  [InterviewDialogMode.SCREEN_SCHEDULED]: {
    title: "Schedule Screen",
    interviewType: InterviewType.INTERNAL,
    candidacyStatus: () => CandidacyStatus.SCREENSCHEDULED,
    defaultInterviewTitle: "Initial Phone Screen",
    defaultStatus: InterviewStatus.CANDIDATE_CONFIRMED,
    defaultDurationMinutes: InterviewDuration.MINUTES_15,
    defaultTimezone: undefined,
    getDefaultDateTime: () => "",
    showStatusSelect: false,
    shouldCreateCalendarEvent: () => true,
    showVideoSwitch: true,
    submitMixpanelEvent: "Schedule screen",
  },
  [InterviewDialogMode.SCREENED]: {
    title: "Log Completed Screen",
    interviewType: InterviewType.INTERNAL,
    candidacyStatus: () => CandidacyStatus.SCREENED,
    defaultInterviewTitle: "Initial Phone Screen",
    defaultStatus: InterviewStatus.COMPLETED,
    defaultDurationMinutes: InterviewDuration.MINUTES_15,
    defaultTimezone: Timezone.CENTRAL,
    getDefaultDateTime: () =>
      dates.roundedMinutesAgoDatetimeLocal(
        SCREENED_DEFAULT_MINUTES_AGO,
        Timezone.CENTRAL,
      ),
    showStatusSelect: false,
    shouldCreateCalendarEvent: () => false,
    showVideoSwitch: false,
    submitMixpanelEvent: "Log completed screen",
  },
};

const interviewSchema = z.object({
  title: z.string().min(1, "Required"),
  description: z.string(),
  dateTime: z.string().min(1, "Required"),
  durationMinutes: z.nativeEnum(InterviewDuration, {
    required_error: "Required",
  }),
  status: z.nativeEnum(InterviewStatus, {
    required_error: "Required",
  }),
  timezone: z.nativeEnum(Timezone, {
    required_error: "Required",
  }),
  isVideo: z.boolean(),
});

export const InterviewDialog = ({
  mode = InterviewDialogMode.INTERVIEW,
  onOpenChange,
  onStatusChanged,
  open,
}: {
  mode?: InterviewDialogMode;
  onOpenChange: (open: boolean) => void;
  onStatusChanged?: (status: CandidacyStatus) => void;
  open: boolean;
}) => {
  const config = modeConfig[mode];

  /* Context */
  const { candidacyId, companyId: currentCompanyId, job } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });

  const queryClient = useQueryClient();

  /* Database CRUD Operations */
  // Create
  const {
    mutate: createAddInterview,
    isPending,
    reset: resetCreateAddInterview,
  } = useCreateAddInterview({
    companyId: currentCompanyId,
    jobId: job.id,
    queryParams: defaultInterviewQueryParams,
  });

  /* Cache Updates */
  // Set
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();

  const form = useForm<z.infer<typeof interviewSchema>>({
    resolver: zodResolver(interviewSchema),
    defaultValues: {
      title: config.defaultInterviewTitle,
      description: "",
      dateTime: config.getDefaultDateTime(),
      durationMinutes: config.defaultDurationMinutes,
      status: config.defaultStatus,
      timezone: config.defaultTimezone,
      isVideo: false,
    },
  });

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof interviewSchema>) => {
    mixpanelUtils.trackFormSubmission(
      config.submitMixpanelEvent,
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        has_description: !!data.description,
        status: data.status,
        duration_minutes: data.durationMinutes,
        type: config.interviewType,
      },
    );
    createAddInterview(
      {
        createInterviewRequest: {
          title: data.title,
          description: data.description || undefined,
          dateTime:
            dates.datetimeLocalToIsoInTimezone(data.dateTime, data.timezone) ??
            new Date(data.dateTime).toISOString(),
          durationMinutes: parseInt(data.durationMinutes, 10),
          status: data.status,
          candidacyId,
          timezone: data.timezone,
          type: config.interviewType,
          isStafferAgentScheduled: false,
          createGoogleCalendarEvent: config.shouldCreateCalendarEvent(
            data.status,
          ),
          createVideoConference:
            data.isVideo && config.shouldCreateCalendarEvent(data.status),
        },
      },
      {
        onSuccess: () => {
          // Only advance the candidacy in cache - never move a candidacy status backwards
          // Prevents backward stage moves when staffers are adding screens/interview that occurred in past
          const statusRank = Object.values(CandidacyStatus);
          const nextCandidacyStatus = config.candidacyStatus(data.status);
          const movesForward =
            !!candidacyInfo &&
            statusRank.indexOf(nextCandidacyStatus) >
              statusRank.indexOf(candidacyInfo.status);
          if (candidacyInfo && movesForward) {
            setCandidacyInAllLists({
              candidacyId,
              lastCandidacyStatusChange: {
                createdAt: new Date().toISOString(),
                fromStatus: candidacyInfo.status,
                toStatus: nextCandidacyStatus,
                statusChangeReason: null,
                byAgent: false,
              },
            });
            setCandidacyInfo({
              candidacyId,
              status: nextCandidacyStatus,
              lastStatusChangeByAgent: false,
            });
            onStatusChanged?.(nextCandidacyStatus);
          }

          // Invalidate all interview queries for this job so any table variant refetches.
          void queryClient.invalidateQueries({
            queryKey: ["interviews", currentCompanyId, job.id],
          });

          form.reset();
          onOpenChange(false);
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Dialog
        onOpenChange={(open) => {
          form.reset();
          resetCreateAddInterview();
          onOpenChange(open);
        }}
        open={open}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{config.title}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              className="flex min-w-0 flex-col gap-4"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(handleSubmit)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  void form.handleSubmit(handleSubmit)();
                }
              }}
            >
              <FormTextInput fieldName="title" form={form} label="Title" />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Description (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Add any notes or details about the interview."
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {config.showStatusSelect ? (
                <FormEnumSelect
                  fieldName="status"
                  form={form}
                  label="Status"
                  options={
                    config.statusOptionValues
                      ? interviewStatusOptions.filter((o) =>
                          config.statusOptionValues?.includes(o.value),
                        )
                      : interviewStatusOptions
                  }
                  placeholder="-"
                />
              ) : null}
              <FormDateTimePicker
                fieldName="dateTime"
                form={form}
                label="Date and Time"
                description="In the candidacy's timezone"
                minuteStep={5}
              />
              <FormEnumSelect
                fieldName="durationMinutes"
                form={form}
                label="Duration"
                options={interviewDurationOptions}
                placeholder="-"
              />
              <FormButtonGroup
                fieldName="timezone"
                form={form}
                label="Timezone"
                description="Select the candidacy's timezone"
                options={timezoneOptions}
              />
              {config.showVideoSwitch ? (
                <FormSwitchInput
                  fieldName="isVideo"
                  form={form}
                  label="Video call (Google Meet)"
                  infoTooltip="Adds a Google Meet link to the calendar event and the screen's description."
                />
              ) : null}
              <Row className="justify-end">
                <Button
                  className="rounded-full"
                  disabled={isPending}
                  type="submit"
                >
                  {isPending ? (
                    <LoadingIndicator gearColor="white" variant="small" />
                  ) : (
                    <>
                      Submit
                      <KeyboardShortcut text="⌘↵" />
                    </>
                  )}
                </Button>
              </Row>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
